import { LoaderCircle, Search, Sparkles, XIcon } from 'lucide-react';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RetryQueryAlert } from 'src/components/alerts/RetryQueryAlert';
import { Button } from 'src/components/ui/button';
import { CardTitle } from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { NoveltiesSkeleton } from 'src/features/novelties/components/NoveltiesSkeleton';
import { NoveltyProductCard } from 'src/features/novelties/NoveltyProductCard';
import { QUERY_KEY_NOVELTIES_BEST_CANDIDATES } from 'src/keys';
import { useNoveltiesBestCandidatesQuery } from 'src/services/api/getNoveltiesBestCandidates';

type NoveltiesProps = {
  search: string;
  localSearch: string;
  setSearch: (value: string) => void;
};

export function Novelties({
  search,
  localSearch,
  setSearch,
}: NoveltiesProps): ReactElement {
  const { t } = useTranslation('novelties');
  const {
    data: novelties,
    isLoading,
    isFetching,
    isError,
  } = useNoveltiesBestCandidatesQuery(search);
  const [expandedSku, setExpandedSku] = useState<string | null>(null);

  const handleToggleExpand = (sku: string) => {
    setExpandedSku((prev) => (prev === sku ? null : sku));
  };

  return (
    <div className='flex h-[calc(100vh-65px)] w-full flex-col gap-6 p-6'>
      <div className='flex h-full flex-col'>
        <div className='pb-4'>
          <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
            <div className='flex items-center gap-3'>
              <Sparkles className='size-6 text-primary-default' />
              <CardTitle className='text-xl'>
                {t('Novelties Best Candidates')}
              </CardTitle>
              {novelties && (
                <span className='bg-primary-default/10 rounded-full px-3 py-1 text-sm font-medium text-primary-default'>
                  {novelties.length} {t('products')}
                </span>
              )}
              {isFetching && (
                <LoaderCircle className='size-4 animate-spin text-layout-foreground-secondary' />
              )}
            </div>
            <div className='relative w-full md:w-80'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-layout-foreground-secondary' />
                <Input
                  type='search'
                  placeholder={t('Search by SKU or title...')}
                  value={localSearch}
                  onChange={(e) => setSearch(e.target.value)}
                  className='pl-10 pr-10'
                />
                {localSearch && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                    onClick={() => setSearch('')}
                  >
                    <XIcon className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        {isError && (
          <div className='pb-4'>
            <RetryQueryAlert
              queryKey={[QUERY_KEY_NOVELTIES_BEST_CANDIDATES, search]}
              message={t('Failed to load novelties')}
              isLoading={isFetching}
            />
          </div>
        )}
        {isLoading && !novelties ? (
          <NoveltiesSkeleton withHeader={false} />
        ) : isError && !novelties ? (
          <div className='flex h-full items-center justify-center' />
        ) : !novelties || novelties.length === 0 ? (
          <div className='flex h-full items-center justify-center'>
            <div className='flex flex-col items-center gap-2 text-center'>
              <h3 className='text-lg font-semibold'>
                {localSearch ? t('No results found') : t('No novelties found')}
              </h3>
              <p className='text-muted-foreground text-sm'>
                {localSearch
                  ? t('Try adjusting your search term')
                  : t('There are no novelties with best candidates yet')}
              </p>
            </div>
          </div>
        ) : (
          <ScrollArea className='h-full'>
            <div className='space-y-3 pr-4'>
              {novelties.map((product) => (
                <NoveltyProductCard
                  key={product.sku}
                  product={product}
                  isExpanded={expandedSku === product.sku}
                  onToggleExpand={() => handleToggleExpand(product.sku)}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
